import { Skeleton } from '@/components/ui/skeleton';
import { useBiddingStrategyReportQuery } from '@/lib/query-options';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

interface BiddingStrategyPerformanceProps {
  dateRange?: string;
  campaignId?: number;
}

const DEFAULT_DATE_RANGE = 'LAST_7_DAYS';

const STRATEGY_TYPE_LABELS: Record<string, string> = {
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  MAXIMIZE_CLICKS: 'Maximize Clicks',
  MAXIMIZE_CONVERSIONS: 'Maximize Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Maximize Conversion Value',
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  TARGET_SPEND: 'Maximize Clicks',
  TARGET_IMPRESSION_SHARE: 'Target Impression Share',
  COMMISSION: 'Commission',
  PERCENT_CPC: 'Percent CPC',
};

export function BiddingStrategyPerformance({ dateRange = DEFAULT_DATE_RANGE, campaignId }: BiddingStrategyPerformanceProps) {
  const { data, isLoading, error } = useBiddingStrategyReportQuery(dateRange, campaignId);
  const { symbol: currencySymbol } = useCurrencySymbol('google');

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-4 h-[72px]">
          <h3 className="text-lg font-semibold text-gray-500">Bidding Strategy Report</h3>
        </div>
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow-sm border border-red-200 rounded-lg overflow-hidden">
        <div className="p-6 flex items-center gap-3 text-red-600">
          <span className="text-sm">Error loading bidding strategy data. Please try again later.</span>
        </div>
      </div>
    );
  }

  if (!data || !data.strategies || data.strategies.length === 0) {
    return (
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-500">Bidding Strategy Report</h3>
          </div>
          <div className="text-sm text-gray-500">
            No bidding strategy data available for the selected period.
          </div>
        </div>
      </div>
    );
  }

  // Filter to only show the 3 supported bidding strategies:
  // 1. MAXIMIZE_CONVERSIONS - Maximize Conversions
  // 2. TARGET_ROAS / MAXIMIZE_CONVERSION_VALUE - Target ROAS
  // 3. TARGET_IMPRESSION_SHARE - Target Impression Share (for competitor campaigns)
  const supportedStrategyTypes = [
    'MAXIMIZE_CONVERSIONS',
    'TARGET_ROAS',
    'MAXIMIZE_CONVERSION_VALUE', // This is used for Target ROAS campaigns
    'TARGET_IMPRESSION_SHARE', // This is used for competitor campaigns
  ];

  const strategies = data.strategies.filter((strategy) => {
    if (!strategy.type) return false;
    return supportedStrategyTypes.includes(strategy.type);
  });

  if (strategies.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-4 h-[72px]">
          <h3 className="text-lg font-semibold text-gray-500">Bidding Strategy Report</h3>
        </div>
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="text-sm text-gray-500">
              No supported bidding strategy data available for the selected period.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-4 h-[72px]">
        <h3 className="text-lg font-semibold text-gray-500">Bidding Strategy Report</h3>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Strategy
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Impressions
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Clicks
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Conversions
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  CPA
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {strategies.map((strategy) => {
                const strategyLabel = strategy.type ? STRATEGY_TYPE_LABELS[strategy.type] || strategy.type : 'Unknown';
                const ctr = strategy.impressions > 0 ? (strategy.clicks / strategy.impressions) * 100 : 0;
                const cpc = strategy.clicks > 0 ? strategy.cost / strategy.clicks : 0;
                const cpa = strategy.conversions > 0 ? strategy.cost / strategy.conversions : 0;
                const conversionRate = strategy.clicks > 0 ? (strategy.conversions / strategy.clicks) * 100 : 0;

                // Determine status pill colors
                const getStatusPillClasses = (status: string | null | undefined) => {
                  if (!status) return 'bg-gray-100 text-gray-900';
                  switch (status.toUpperCase().trim()) {
                    case 'ENABLED':
                      return 'bg-green-100 text-green-800';
                    case 'PAUSED':
                      return 'bg-yellow-100 text-yellow-800';
                    default:
                      return 'bg-gray-100 text-gray-900';
                  }
                };

                // Format status text: trim and capitalize first letter
                const formatStatusText = (status: string | null | undefined) => {
                  if (!status) return '';
                  const trimmed = status.trim();
                  if (!trimmed) return '';
                  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
                };

                return (
                  <tr key={strategy.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {strategyLabel}
                      </div>
                      {(strategy.target_cpa || strategy.target_roas) && (
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                          {strategy.target_cpa && (
                            <span>Target CPA: {currencySymbol}{strategy.target_cpa.toFixed(2)}</span>
                          )}
                          {strategy.target_roas && (
                            <span>Target ROAS: {strategy.target_roas.toFixed(2)}x</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm montserrat-medium text-gray-900">{strategyLabel}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {strategy.status ? (
                        <span className={`inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium ${getStatusPillClasses(strategy.status)}`}>
                          {formatStatusText(strategy.status)}
                        </span>
                      ) : (
                        <span className="text-sm montserrat-medium text-gray-900">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm montserrat-medium text-gray-900">
                        {strategy.impressions.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm montserrat-medium text-gray-900">
                        {strategy.clicks.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">CTR: {ctr.toFixed(2)}%</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm montserrat-medium text-gray-900">
                        {currencySymbol}{strategy.cost.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">CPC: {currencySymbol}{cpc.toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm montserrat-medium text-gray-900">
                        {strategy.conversions.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">Rate: {conversionRate.toFixed(2)}%</div>
                      {strategy.all_conversions !== undefined &&
                       strategy.all_conversions !== null &&
                       strategy.all_conversions > 0 &&
                       strategy.all_conversions > strategy.conversions && (
                        <div className="text-xs text-blue-600 mt-1">
                          All: {strategy.all_conversions.toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm montserrat-medium text-gray-900">
                        {strategy.conversions > 0 ? `${currencySymbol}${cpa.toFixed(2)}` : '-'}
                      </div>
                      {strategy.conversions_value > 0 && (
                        <div className="text-xs text-gray-500">
                          Value: {currencySymbol}{strategy.conversions_value.toFixed(2)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

